import { styled } from "goober";
import { useLayoutEffect, useRef, useState } from "react";
import { InfoIcon } from "./InfoIcon";
import { SegmentTooltip } from "./SegmentTooltip";
import type { StatusSegment } from "./StatusBreakdownChart";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";

const BASE_SIZE = 240;
// Room reserved around the web for the axis labels.
const LABEL_MARGIN = 32;
const LEVELS = 4;
const GRID = "var(--text-primary)";
const GRID_OPACITY = 0.12;
const GHOST_FRACTIONS = [0.55, 0.35, 0.7, 0.45, 0.6, 0.5];

const Outer = styled("div")`
  display: flex;
  flex: 1;
  min-height: 0;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const Board = styled("div")`
  position: relative;
  flex-shrink: 0;
`;

const Hit = styled("path")`
  fill: transparent;
`;

const LabelCell = styled("div")`
  position: absolute;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`;

const LabelText = styled("span")`
  font-size: 10px;
  line-height: 1;
  transition: color 120ms ease;
`;

const DataLine = styled("polygon")`
  transition: stroke 120ms ease;
`;

// paint-order: stroke draws the border behind the fill, so the 2px border sits
// fully outside the dot (the fill keeps its full 2r diameter) instead of a
// centered stroke that straddles the edge and shrinks the visible dot.
const Dot = styled("circle")`
  paint-order: stroke;
  transition:
    r 120ms ease,
    fill 120ms ease;
`;

type Props = {
  segments: ReadonlyArray<StatusSegment>;
  onSegmentClick?: (segment: StatusSegment) => void;
  placeholder?: boolean;
};

export function RadarChart({ segments, onSegmentClick, placeholder }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () =>
      setBox({ width: el.clientWidth, height: el.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const n = segments.length;
  const size =
    box.width > 0 && box.height > 0
      ? Math.min(box.width, box.height)
      : BASE_SIZE;
  const c = size / 2;
  const r = Math.max(c - LABEL_MARGIN, 1);
  const maxValue = Math.max(1, ...segments.map((s) => s.count));

  // Axis i starts at the top (12 o'clock) and steps clockwise.
  const angleOf = (i: number) => -Math.PI / 2 + (i * 2 * Math.PI) / n;
  const pointAt = (i: number, radius: number) => {
    const a = angleOf(i);
    return { x: c + radius * Math.cos(a), y: c + radius * Math.sin(a) };
  };

  const hovered =
    hoveredKey != null
      ? (segments.find((s) => s.key === hoveredKey) ?? null)
      : null;
  const isHovering = hoveredKey != null;

  if (n < 3) {
    return <Outer ref={ref} aria-hidden={placeholder || undefined} />;
  }

  const valueRadius = (s: StatusSegment, i: number) =>
    (placeholder
      ? GHOST_FRACTIONS[i % GHOST_FRACTIONS.length]!
      : s.count / maxValue) * r;

  const polygon = segments
    .map((s, i) => {
      const p = pointAt(i, valueRadius(s, i));
      return `${p.x},${p.y}`;
    })
    .join(" ");

  return (
    <Outer ref={ref} aria-hidden={placeholder || undefined}>
      <Board style={{ width: size, height: size }}>
        <svg width={size} height={size} style={{ overflow: "visible" }}>
          {Array.from({ length: LEVELS }, (_, level) => (
            <circle
              // oxlint-disable-next-line react/no-array-index-key -- fixed ring count
              key={level}
              cx={c}
              cy={c}
              r={(r * (level + 1)) / LEVELS}
              fill="none"
              stroke={GRID}
              strokeOpacity={GRID_OPACITY}
              strokeWidth={0.5}
            />
          ))}
          {segments.map((s, i) => {
            const p = pointAt(i, r);
            return (
              <line
                key={`spoke-${s.key}`}
                x1={c}
                y1={c}
                x2={p.x}
                y2={p.y}
                stroke={GRID}
                strokeOpacity={GRID_OPACITY}
                strokeWidth={0.5}
              />
            );
          })}
          <DataLine
            points={polygon}
            fill={placeholder ? "var(--alpha8)" : "var(--text-primary)"}
            fillOpacity={placeholder ? 1 : 0.1}
            strokeWidth={1}
            strokeLinejoin="round"
            style={{
              stroke: placeholder
                ? "var(--alpha16)"
                : isHovering
                  ? "var(--gray-600)"
                  : "var(--text-primary)",
            }}
          />
          {segments.map((s, i) => {
            const p = pointAt(i, valueRadius(s, i));
            const active = hoveredKey === s.key;
            return (
              <Dot
                key={`dot-${s.key}`}
                cx={p.x}
                cy={p.y}
                stroke="var(--surface-secondary)"
                // 4px stroke, but paint-order hides its inner half under the
                // fill, leaving a 2px border outside the 6px / 12px dot.
                strokeWidth={4}
                style={{
                  r: active ? 6 : 3,
                  fill: placeholder
                    ? "var(--alpha16)"
                    : active
                      ? "var(--text-primary)"
                      : isHovering
                        ? "var(--gray-600)"
                        : "var(--text-primary)",
                }}
              />
            );
          })}
          {!placeholder &&
            segments.map((s, i) => {
              const a0 = angleOf(i) - Math.PI / n;
              const a1 = angleOf(i) + Math.PI / n;
              const p0 = { x: c + r * Math.cos(a0), y: c + r * Math.sin(a0) };
              const p1 = { x: c + r * Math.cos(a1), y: c + r * Math.sin(a1) };
              return (
                <Hit
                  key={`hit-${s.key}`}
                  d={`M${c},${c}L${p0.x},${p0.y}A${r},${r} 0 0 1 ${p1.x},${p1.y}Z`}
                  style={{
                    cursor: onSegmentClick != null ? "pointer" : "default",
                  }}
                  onMouseEnter={(e) => {
                    setHoveredKey(s.key);
                    setPointer({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseMove={(e) =>
                    setPointer({ x: e.clientX, y: e.clientY })
                  }
                  onMouseLeave={() => {
                    setHoveredKey(null);
                    setPointer(null);
                  }}
                  onClick={onSegmentClick ? () => onSegmentClick(s) : undefined}
                />
              );
            })}
        </svg>
        {segments.map((s, i) => {
          const lp = pointAt(i, r + 14);
          const ca = Math.cos(angleOf(i));
          const sa = Math.sin(angleOf(i));
          return (
            <LabelCell
              key={`label-${s.key}`}
              style={{
                left: lp.x,
                top: lp.y,
                transform: `translate(${-50 + ca * 50}%, ${-50 + sa * 50}%)`,
              }}
            >
              <LabelText
                style={{
                  color:
                    hoveredKey === s.key
                      ? "var(--text-primary)"
                      : "var(--text-secondary)",
                  cursor:
                    !placeholder && onSegmentClick ? "pointer" : "default",
                }}
                onMouseEnter={
                  placeholder
                    ? undefined
                    : (e) => {
                        setHoveredKey(s.key);
                        setPointer({ x: e.clientX, y: e.clientY });
                      }
                }
                onMouseMove={
                  placeholder
                    ? undefined
                    : (e) => setPointer({ x: e.clientX, y: e.clientY })
                }
                onMouseLeave={
                  placeholder
                    ? undefined
                    : () => {
                        setHoveredKey(null);
                        setPointer(null);
                      }
                }
                onClick={
                  !placeholder && onSegmentClick
                    ? () => onSegmentClick(s)
                    : undefined
                }
              >
                {s.label}
              </LabelText>
              {!placeholder && s.note ? (
                <Tooltip
                  content={
                    <div style={{ width: 200 }}>
                      <Text variant="text-small-md">{s.note}</Text>
                    </div>
                  }
                >
                  <InfoIcon />
                </Tooltip>
              ) : null}
            </LabelCell>
          );
        })}
        {hovered && pointer ? (
          <SegmentTooltip segment={hovered} x={pointer.x} y={pointer.y} />
        ) : null}
      </Board>
    </Outer>
  );
}
